/** biome-ignore-all lint/a11y/useSemanticElements: created a custom listbox */
import { CzechiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Czechia";
import { GermanyFlag } from "Components/Shared/LanguageSwitcher/Flags/Germany";
import { GreatBritainFlag } from "Components/Shared/LanguageSwitcher/Flags/GreatBritain";
import { PolandFlag } from "Components/Shared/LanguageSwitcher/Flags/Poland";
import { SlovakiaFlag } from "Components/Shared/LanguageSwitcher/Flags/Slovakia";
import { VietnamFlag } from "Components/Shared/LanguageSwitcher/Flags/Vietnam";
import type { Language } from "Translations/languageConfig";
import { Resources, useResource } from "Translations/Resources";
import { useLanguage } from "Translations/useLanguage";
import { Box, ButtonBase, Chip, Typography } from "@mui/material";
import { type ReactNode, useRef } from "react";

const LANGUAGES: Array<{
  code: Language;
  label: string;
  flag: ReactNode;
  isImplemented: boolean;
}> = [
  {
    code: "cs",
    label: "Čeština",
    flag: <CzechiaFlag size={25} />,
    isImplemented: true,
  },
  {
    code: "sk",
    label: "Slovenčina",
    flag: <SlovakiaFlag size={25} />,
    isImplemented: true,
  },
  {
    code: "en",
    label: "English",
    flag: <GreatBritainFlag size={25} />,
    isImplemented: true,
  },
  {
    code: "vi",
    label: "Tiếng Việt",
    flag: <VietnamFlag size={25} />,
    isImplemented: true,
  },
  {
    code: "pl",
    label: "Polski",
    flag: <PolandFlag size={25} />,
    isImplemented: false,
  },
  {
    code: "de",
    label: "Deutsch",
    flag: <GermanyFlag size={25} />,
    isImplemented: false,
  },
];

const LanguageSwitcher = () => {
  const listRef = useRef<HTMLUListElement>(null);
  const { currentLanguage, changeLanguage } = useLanguage();
  const { t } = useResource();

  return (
    <Box
      component="ul"
      ref={listRef}
      role="listbox"
      aria-label="Select application language"
      sx={{
        listStyle: "none",
        p: 0,
        m: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {LANGUAGES.map(({ code, label, flag, isImplemented }) => {
        const selected = code === currentLanguage;

        return (
          <Box
            key={code}
            component="li"
            role="option"
            aria-selected={selected}
            sx={{ width: "100%" }}
          >
            <ButtonBase
              onClick={() => changeLanguage(code)}
              aria-current={selected ? "true" : undefined}
              disabled={!isImplemented}
              sx={({ palette }) => ({
                width: "100%",
                justifyContent: "space-between",
                alignItems: "center",
                borderRadius: 2,
                border: selected
                  ? `1.5px solid ${palette.primary.main}`
                  : `1.5px solid ${palette.divider}`,
                backgroundColor: isImplemented
                  ? selected
                    ? palette.primary.main
                    : palette.background.paper
                  : palette.grey[200],
                color: selected
                  ? palette.primary.contrastText
                  : palette.text.primary,
                opacity: isImplemented ? 1 : 0.5,
                px: 4,
                py: 2,
                boxShadow: selected ? 1 : 0,
                outline: "none",
                transition: "background 0.2s, border 0.2s, color 0.2s",
                display: "flex",
                cursor: "pointer",
                "&:focus-visible": {
                  boxShadow: `0 0 0 2px ${palette.primary.light}`,
                },
              })}
            >
              <Typography textAlign="left" fontWeight={500} fontSize="1rem">
                {label}
              </Typography>
              <Box component="span" display="flex" alignItems="center" gap={3}>
                {!isImplemented && (
                  <Chip
                    label={t(Resources.Components.LanguageSwitcher.ComingSoon)}
                    size="small"
                    variant="outlined"
                  />
                )}
                {flag}
              </Box>
            </ButtonBase>
          </Box>
        );
      })}
    </Box>
  );
};

export default LanguageSwitcher;
